import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, MessageCircle, Settings, ShieldCheck } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import WhatsAppSetupReadinessPanel from '../../components/portal/WhatsAppSetupReadinessPanel';

export function canManageWorkspaceIntegrations(user) {
  return user?.role === 'admin' || user?.role === 'super_admin';
}

export default function SettingsIntegrationsPage() {
  const { user } = useAuth();
  const canManage = canManageWorkspaceIntegrations(user);

  if (!canManage) {
    return (
      <div className="space-y-4" data-testid="settings-integrations-page">
        <Button asChild variant="ghost" size="sm">
          <Link to="/portal/settings">
            <ArrowLeft className="mr-2 h-4 w-4" /> Workspace Settings
          </Link>
        </Button>
        <Card className="border-amber-200 bg-amber-50 shadow-sm">
          <CardContent className="p-5">
            <p className="font-semibold text-amber-950">Integrations are admin-only</p>
            <p className="mt-1 text-sm text-amber-900">
              Ask a workspace admin or owner to review provider configuration and connection readiness.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-integrations-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-primary">
            <Settings className="h-4 w-4" /> Settings
          </p>
          <h1 className="mt-1 text-3xl font-heading font-semibold text-text-primary">Integrations</h1>
          <p className="mt-1 max-w-3xl text-text-muted">
            Configure and verify external services used by CareOps. Provider setup belongs here, not inside applicant, staff or client records.
          </p>
        </div>
        <Button asChild variant="outline" className="shrink-0 rounded-lg">
          <Link to="/portal/settings">
            <ArrowLeft className="mr-2 h-4 w-4" /> Workspace Settings
          </Link>
        </Button>
      </div>

      <Card className="border-[#E4E8EB] shadow-sm" data-testid="whatsapp-integration-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-heading">
            <MessageCircle className="h-5 w-5 text-primary" />
            Meta WhatsApp Cloud
          </CardTitle>
          <p className="text-sm text-text-muted">
            Shared Meta provider configuration with separate approved-template domains for Recruitment and Staff Communications.
          </p>
        </CardHeader>
        <CardContent>
          <WhatsAppSetupReadinessPanel />
          <div className="mt-4 flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/portal/staff-communications">
                <ExternalLink className="mr-2 h-4 w-4" /> Test Staff WhatsApp
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/portal/inbox?department=recruitment">
                <ExternalLink className="mr-2 h-4 w-4" /> Open Recruitment Inbox
              </Link>
            </Button>
          </div>
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              Applicant conversations and templates stay in Recruitment. Active workforce conversations and staff-approved templates stay in Staff Communications. Sharing the Meta provider never merges those records or template catalogues.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
